/**
 * CallHistoryDialog — mobile-phone-style call log popup, opened from the
 * "Call History" button next to "AI Orders" on My Chats.
 *
 * Live data via:
 *   GET /api/ai/voice/call-history-agents  — org list for the cascading
 *       Manager → Team Lead → Agent filters (empty for a plain agent viewer,
 *       who only ever sees their own history).
 *   GET /api/ai/voice/call-history         — one page of calls for a target
 *       agent (default: viewer's own) + today's incoming/outgoing/talk-time
 *       stats. Infinite-scrolled, most-recent-first.
 *
 * Both routes are JWT-gated — no viewerId/agentId spoofing: the backend
 * re-derives the viewer from the token and only honors ?agentId= when it's
 * the viewer themself or inside their own org subtree (or the viewer is a
 * top supervisor). See manijment-api services/ai-service/routes.js.
 *
 * Each row also shows:
 *   - the call's `disposition` (callStatus/outcomeCode + remark) — what the
 *     agent filled in the post-call form, best-effort joined server-side by
 *     phone number (no call-id FK exists — see routes.js's call-history join).
 *   - a recording play button, when `recordingUrl` is present.
 *   - a click-to-call button to redial that row's number via the softphone
 *     (<SoftphoneButton cohort />) — shown ONLY to a cohort-team caller:
 *     agent (userType 5) or team lead (13) whose own admin doc has
 *     cohortTeam:true (localStorage 'cohortTeam', set at login).
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CallMadeIcon from '@material-ui/icons/CallMade';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import CallMissedIcon from '@material-ui/icons/CallMissed';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import EventIcon from '@material-ui/icons/Event';
import { getDecryptDataFromLocal } from '../../utils/encrypt';
import { SoftphoneButton } from '../../ui-component/SoftphoneCall';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const getAdminId = () => {
  try { return localStorage.getItem('adminId') || null; }
  catch (e) { return null; }
};

function fmtDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
}

function fmtClock(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Follow-up date/time the agent set when marking HOT/WARM (new-revenue) or any
// cohort outcome with a promised callback — CallReports.followUpTime.
function fmtFollowUp(d) {
  if (!d) return '';
  const date = new Date(d);
  const datePart = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
  const timePart = date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
}

function dayLabel(d) {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

// direction + connectedAt -> { Icon, color, label }
function callVisual(call) {
  const connected = Boolean(call.connectedAt);
  if (call.direction === 'inbound') {
    return connected
      ? { Icon: CallReceivedIcon, color: '#2e7d32', label: 'Incoming' }
      : { Icon: CallMissedIcon, color: '#d32f2f', label: 'Missed' };
  }
  return connected
    ? { Icon: CallMadeIcon, color: '#1565c0', label: 'Outgoing' }
    : { Icon: CallMadeIcon, color: '#9e9e9e', label: 'Not connected' };
}

// Disposition = what the agent picked in the post-call form (CallDispositionDialog
// / CohortCallDisposition), best-effort joined server-side by phone number — see
// GET /api/ai/voice/call-history. outcomeCode is the cohort team's own taxonomy
// (WILL_PLACE_LATER_HOT, OLD_STOCK, ...); callStatus is the legacy fallback label
// every call gets regardless of team.
function dispositionLabel(d) {
  if (!d) return null;
  return d.outcomeCode || d.callStatus || null;
}

const selectSx = { minWidth: 170, height: 34, background: '#fff', fontSize: 13 };

export default function CallHistoryDialog({ open, onClose }) {
  const myAdminId = getAdminId();
  const myUserType = useMemo(() => {
    try { return Number(getDecryptDataFromLocal('setRole')) || 0; } catch (e) { return 0; }
  }, []);
  // Click-to-call from a history row — every agent (5) and team lead (13)
  // (2026-10-15; was cohort-only). cohortTeam (localStorage flag set at login
  // from admin.cohortTeam, same one UnregisteredLeadsPopup / AddLeadsForAgent /
  // SaleDashboard read) only picks WHICH post-call form opens: the cohort
  // outcome form for the repeat team, the new-revenue one for everyone else.
  const canClickToCall = myUserType === 5 || myUserType === 13;
  const isCohortCaller = localStorage.getItem('cohortTeam') === 'true';
  const [openRecordingId, setOpenRecordingId] = useState(null);

  const [admins, setAdmins]           = useState([]);
  const [adminsLoaded, setAdminsLoaded] = useState(false);
  const [managerFilter, setManagerFilter]   = useState('');
  const [teamLeadFilter, setTeamLeadFilter] = useState('');
  const [agentFilter, setAgentFilter]       = useState(''); // '' = self

  const [calls, setCalls]         = useState([]);
  const [stats, setStats]         = useState(null);
  const [targetMeta, setTargetMeta] = useState(null);
  const [directionFilter, setDirectionFilter] = useState(''); // '' | 'inbound' | 'outbound'
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore]       = useState(false);
  const [error, setError]         = useState(null);

  const byId = useMemo(() => {
    const m = new Map();
    admins.forEach(a => m.set(String(a._id), a));
    return m;
  }, [admins]);

  const managers = useMemo(() => admins.filter(a => Number(a.userType) === 14), [admins]);

  const teamLeads = useMemo(() => {
    return admins.filter(a => {
      if (Number(a.userType) !== 13) return false;
      if (!managerFilter) return true;
      return String(a.immediateSeniorId || '') === managerFilter;
    });
  }, [admins, managerFilter]);

  const agentBelongsToManager = useCallback((a, mgrId) => {
    if (!mgrId) return true;
    const seniorId = String(a.immediateSeniorId || '');
    if (seniorId === mgrId) return true;
    const senior = byId.get(seniorId);
    return Boolean(senior && String(senior.immediateSeniorId || '') === mgrId);
  }, [byId]);

  const agents = useMemo(() => {
    return admins.filter(a => {
      if (Number(a.userType) !== 5) return false;
      if (teamLeadFilter) return String(a.immediateSeniorId || '') === teamLeadFilter;
      return agentBelongsToManager(a, managerFilter);
    });
  }, [admins, managerFilter, teamLeadFilter, agentBelongsToManager]);

  // The bottom-level select: a team lead's OWN calls (not just their agents')
  // are selectable wherever that team lead shows up as a filter option — a
  // team lead places calls too, same as an agent. Shailendra 2026-08-31.
  const agentSelectOptions = useMemo(() => {
    const own = teamLeads
      .filter(tl => !teamLeadFilter || String(tl._id) === teamLeadFilter)
      .map(tl => ({ _id: String(tl._id), name: `★ ${tl.name} (own calls)` }));
    return [...own, ...agents.map(a => ({ _id: String(a._id), name: a.name }))];
  }, [teamLeads, teamLeadFilter, agents]);

  // Which admin's history are we actually viewing right now.
  const effectiveAgentId = agentFilter || myAdminId;

  useEffect(() => {
    if (!open || adminsLoaded) return;
    axios.get(`${AI_PATH}/api/ai/voice/call-history-agents`)
      .then(res => setAdmins(res.data?.admins || []))
      .catch(() => setAdmins([]))
      .finally(() => setAdminsLoaded(true));
  }, [open, adminsLoaded]);

  const fetchPage = useCallback((pageNum, append) => {
    if (!effectiveAgentId) return;
    (pageNum === 1 ? setLoadingInitial : setLoadingMore)(true);
    setError(null);
    axios.get(`${AI_PATH}/api/ai/voice/call-history`, {
      params: { agentId: effectiveAgentId, page: pageNum, per: 30, direction: directionFilter || undefined },
    })
      .then(res => {
        const d = res.data || {};
        setCalls(prev => append ? [...prev, ...(d.calls || [])] : (d.calls || []));
        setHasMore(Boolean(d.hasMore));
        setPage(pageNum);
        if (d.stats) setStats(d.stats);
        if (d.target) setTargetMeta(d.target);
      })
      .catch(e => setError(e.message))
      .finally(() => (pageNum === 1 ? setLoadingInitial : setLoadingMore)(false));
  }, [effectiveAgentId, directionFilter]);

  useEffect(() => {
    if (!open || !effectiveAgentId) return;
    setCalls([]);
    setStats(null);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, effectiveAgentId, directionFilter]);

  const scrollRef = useRef(null);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || loadingMore || loadingInitial || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      fetchPage(page + 1, true);
    }
  };

  const showManagerFilter  = managers.length > 0;
  const showTeamLeadFilter = teamLeads.length > 0 && (myUserType === 14 || managerFilter || myUserType === 1 || myUserType === 17);
  const showAgentFilter    = agentSelectOptions.length > 0;

  const resetOnClose = () => {
    onClose();
  };

  let lastDay = null;

  return (
    <Dialog open={open} onClose={resetOnClose} maxWidth="sm" fullWidth PaperProps={{ style: { height: '85vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle style={{ paddingBottom: 8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>📞 Call History{targetMeta?.name ? ` — ${targetMeta.name}` : ''}</span>
          <IconButton size="small" onClick={resetOnClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      {/* Filters — only the ones this viewer's role is allowed to see. */}
      {(showManagerFilter || showTeamLeadFilter || showAgentFilter) && (
        <Box px={3} pb={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {showManagerFilter && (
            <Select
              variant="outlined"
              displayEmpty
              value={managerFilter}
              onChange={(e) => { setManagerFilter(e.target.value); setTeamLeadFilter(''); setAgentFilter(''); }}
              style={selectSx}
            >
              <MenuItem value="">All Managers</MenuItem>
              {managers.map(m => <MenuItem key={m._id} value={String(m._id)}>{m.name}</MenuItem>)}
            </Select>
          )}
          {showTeamLeadFilter && (
            <Select
              variant="outlined"
              displayEmpty
              value={teamLeadFilter}
              onChange={(e) => { setTeamLeadFilter(e.target.value); setAgentFilter(''); }}
              style={selectSx}
            >
              <MenuItem value="">All Team Leads</MenuItem>
              {teamLeads.map(tl => <MenuItem key={tl._id} value={String(tl._id)}>{tl.name}</MenuItem>)}
            </Select>
          )}
          {showAgentFilter && (
            <Select
              variant="outlined"
              displayEmpty
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              style={selectSx}
            >
              <MenuItem value="">★ My own calls</MenuItem>
              {agentSelectOptions.map(a => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
            </Select>
          )}
        </Box>
      )}

      {/* Today's summary strip. Incoming/Outgoing are clickable filters — a
          high-volume outbound dialer buries a day's few incoming calls
          hundreds of rows deep in the plain list, so tapping the chip is the
          only practical way to actually see them. */}
      <Box px={3} pb={1.5} display="flex" alignItems="center" style={{ gap: 10 }}>
        <StatChip
          label="Incoming" value={stats ? `${stats.incomingConnected}/${stats.incomingCount}` : '—'} color="#2e7d32"
          active={directionFilter === 'inbound'}
          onClick={() => setDirectionFilter(directionFilter === 'inbound' ? '' : 'inbound')}
        />
        <StatChip
          label="Outgoing" value={stats ? `${stats.outgoingConnected}/${stats.outgoingCount}` : '—'} color="#1565c0"
          active={directionFilter === 'outbound'}
          onClick={() => setDirectionFilter(directionFilter === 'outbound' ? '' : 'outbound')}
        />
        <StatChip label="Talk time today" value={stats ? fmtDuration(stats.talkTimeTodaySec) : '—'} color="#6a1b9a" />
        {directionFilter && (
          <Typography
            variant="caption"
            onClick={() => setDirectionFilter('')}
            style={{ color: '#6b7280', cursor: 'pointer', textDecoration: 'underline', marginLeft: 'auto' }}
          >
            Clear filter
          </Typography>
        )}
      </Box>

      <DialogContent
        ref={scrollRef}
        onScroll={onScroll}
        style={{ padding: 0, borderTop: '1px solid #eee', background: '#fafafa', flex: 1 }}
      >
        {loadingInitial && (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress size={26} /></Box>
        )}
        {error && !loadingInitial && (
          <Typography variant="body2" style={{ color: '#b71c1c', padding: 16 }}>Failed to load: {error}</Typography>
        )}
        {!loadingInitial && !error && calls.length === 0 && (
          <Typography variant="body2" style={{ color: '#6b7280', padding: 24, textAlign: 'center' }}>No calls yet.</Typography>
        )}
        {calls.map((c) => {
          const other = c.direction === 'inbound' ? c.fromPhone : c.toPhone;
          const { Icon, color, label } = callVisual(c);
          const anchorDate = c.startedAt || c.createdAt;
          const label_ = dayLabel(anchorDate);
          const showDivider = label_ !== lastDay;
          lastDay = label_;
          const ringingSec = c.connectedAt && c.startedAt
            ? (new Date(c.connectedAt) - new Date(c.startedAt)) / 1000
            : (c.endedAt && c.startedAt ? (new Date(c.endedAt) - new Date(c.startedAt)) / 1000 : null);
          return (
            <React.Fragment key={c._id}>
              {showDivider && (
                <Box style={{ textAlign: 'center', padding: '10px 0 4px' }}>
                  <span style={{
                    background: '#e0e0e0', color: '#555', fontSize: 11, fontWeight: 700,
                    padding: '2px 10px', borderRadius: 10,
                  }}>{label_}</span>
                </Box>
              )}
              <Box
                style={{ padding: '10px 20px', background: '#fff', borderBottom: '1px solid #f0f0f0' }}
              >
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 38, height: 38, borderRadius: '50%', background: `${color}1a`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 14, flexShrink: 0,
                    }}
                  >
                    <Icon style={{ color, fontSize: 20 }} />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography style={{ fontWeight: 700, fontSize: 14.5, color: '#1f2937' }}>{other || '—'}</Typography>
                    <Typography variant="caption" style={{ color, fontWeight: 600 }}>
                      {label}
                      {ringingSec != null && `  ·  ring ${fmtDuration(ringingSec)}`}
                      {c.connectedAt && `  ·  talk ${fmtDuration(c.talktime)}`}
                    </Typography>
                    {/* Call end disposition — what the agent filled in the post-call
                        form, best-effort joined server-side by phone number. */}
                    {dispositionLabel(c.disposition) && (
                      <Typography
                        variant="caption"
                        style={{ display: 'block', color: '#6a1b9a', fontWeight: 700, marginTop: 1 }}
                      >
                        {dispositionLabel(c.disposition)}
                        {c.disposition.callRemark && ` — ${c.disposition.callRemark}`}
                      </Typography>
                    )}
                    {/* HOT/WARM (or any cohort outcome with a promised callback) —
                        the date/time the agent committed to, so it doesn't take a
                        second lookup to see when this lead is due again. */}
                    {c.disposition?.followUpTime && (
                      <Typography
                        variant="caption"
                        style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#e65100', fontWeight: 700, marginTop: 1 }}
                      >
                        <EventIcon style={{ fontSize: 12 }} />
                        Follow-up: {fmtFollowUp(c.disposition.followUpTime)}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ marginLeft: 8, flexShrink: 0 }}>
                    <Typography variant="caption" style={{ color: '#9e9e9e', whiteSpace: 'nowrap' }}>
                      {fmtClock(anchorDate)}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ marginTop: 2 }}>
                      {c.recordingUrl && (
                        <Tooltip title={openRecordingId === c._id ? 'Hide recording' : 'Play recording'}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenRecordingId((id) => (id === c._id ? null : c._id))}
                          >
                            {openRecordingId === c._id
                              ? <VolumeUpIcon fontSize="small" style={{ color: '#6a1b9a' }} />
                              : <PlayCircleOutlineIcon fontSize="small" style={{ color: '#9e9e9e' }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Click-to-call (agents + TLs) — dials this row's number straight
                          from Call History via the softphone. */}
                      {canClickToCall && other && (
                        <SoftphoneButton
                          number={other}
                          cohort={isCohortCaller}
                          title={`Call ${other} on softphone`}
                          size="small"
                          color="#1565c0"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                {openRecordingId === c._id && c.recordingUrl && (
                  <audio controls autoPlay src={c.recordingUrl} style={{ width: '100%', marginTop: 8, height: 32 }}>
                    Your browser does not support audio playback.
                  </audio>
                )}
              </Box>
            </React.Fragment>
          );
        })}
        {loadingMore && (
          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={20} /></Box>
        )}
        {!hasMore && calls.length > 0 && (
          <Typography variant="caption" style={{ color: '#bbb', display: 'block', textAlign: 'center', padding: 14 }}>
            — end of call history —
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatChip({ label, value, color, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      style={{
        background: active ? `${color}26` : `${color}12`,
        border: `1px solid ${active ? color : `${color}33`}`, borderRadius: 8,
        padding: '6px 12px', minWidth: 90,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Typography variant="caption" style={{ color, fontWeight: 700, display: 'block', fontSize: 10.5 }}>{label}</Typography>
      <Typography style={{ color: '#1f2937', fontWeight: 800, fontSize: 15 }}>{value}</Typography>
    </Box>
  );
}
